import { supabase } from "@/integrations/supabase/client";
import type { Profile } from "@/types/profile";

export type UserTier = 'free' | 'pro' | 'beta_pro';

export interface TierLimits {
  max_goals: number;
  max_habits: number;
  max_coach_analyses_per_month: number;
  max_plan_generations: number;
  habit_history_days: number;
  can_message_mentors: boolean;
  can_offer_mentorship: boolean;
  integrations: readonly string[];
}

export const TIER_LIMITS: Record<UserTier, TierLimits> = {
  free: {
    max_goals: 3,
    max_habits: 5,
    max_coach_analyses_per_month: 1,
    max_plan_generations: 1,
    habit_history_days: 30,
    can_message_mentors: false,
    can_offer_mentorship: false,
    integrations: ['google_calendar'],
  },
  pro: {
    max_goals: -1, // -1 means unlimited
    max_habits: -1,
    max_coach_analyses_per_month: -1,
    max_plan_generations: -1,
    habit_history_days: 90,
    can_message_mentors: true,
    can_offer_mentorship: true,
    integrations: ['google_calendar', 'outlook', 'apple_calendar', 'slack', 'notion'],
  },
  beta_pro: {
    max_goals: -1,
    max_habits: -1,
    max_coach_analyses_per_month: -1,
    max_plan_generations: -1,
    habit_history_days: 90,
    can_message_mentors: true,
    can_offer_mentorship: true,
    integrations: ['google_calendar', 'outlook', 'apple_calendar', 'slack', 'notion'],
  }
} as const;

/**
 * Resolve tier from an already-fetched profile object (synchronous).
 *
 * Does NOT auto-downgrade expired beta_pro in the database.
 * Relies on `useDowngradeCheck()` running on page load. See MOMENTUMEQ-WEB-1.
 */
export function getTierFromProfile(profile: Profile | null): UserTier {
  if (!profile) return 'free';

  const tier = (profile.tier as UserTier) || 'free';

  if (tier === 'beta_pro' && profile.beta_pro_expires_at) {
    const expiresAt = new Date(profile.beta_pro_expires_at);
    if (expiresAt < new Date()) {
      return 'free';
    }
  }

  return tier;
}

/**
 * Get tier limits from an already-fetched profile (synchronous).
 */
export function getLimitsFromProfile(profile: Profile | null): TierLimits {
  return TIER_LIMITS[getTierFromProfile(profile)];
}

/**
 * Check if user can run a coach analysis this month, using an already-fetched
 * profile for tier resolution (only hits `usage_limits` table, not profiles).
 */
export async function canRunCoachAnalysisFromProfile(userId: string, profile: Profile | null): Promise<LimitCheckResult> {
  const limits = getLimitsFromProfile(profile);

  if (limits.max_coach_analyses_per_month === -1) {
    return { allowed: true };
  }

  const { data: usage, error } = await supabase
    .from('usage_limits')
    .select('current_count, period_start')
    .eq('user_id', userId)
    .eq('resource_type', 'coach_analyses')
    .maybeSingle();

  if (error) {
    console.error('Error fetching usage:', error);
    return { allowed: false, reason: 'error' };
  }

  if (!usage || !isCurrentMonth(usage.period_start)) {
    return { allowed: true, usedThisMonth: 0, limit: limits.max_coach_analyses_per_month };
  }

  const usedThisMonth = usage.current_count || 0;

  if (usedThisMonth >= limits.max_coach_analyses_per_month) {
    return {
      allowed: false,
      reason: 'coach_limit',
      usedThisMonth,
      limit: limits.max_coach_analyses_per_month
    };
  }

  return { allowed: true, usedThisMonth, limit: limits.max_coach_analyses_per_month };
}

export interface LimitCheckResult {
  allowed: boolean;
  reason?: string;
  currentCount?: number;
  usedThisMonth?: number;
  limit?: number;
}

/**
 * Get user's current tier, handling beta_pro expiration
 */
export async function getUserTier(userId: string): Promise<UserTier> {
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('tier, beta_pro_expires_at')
    .eq('user_id', userId)
    .maybeSingle();

  if (error || !profile) {
    console.error('Error fetching user tier:', error);
    return 'free';
  }

  const tier = (profile.tier as UserTier) || 'free';

  // Check if beta_pro has expired
  if (tier === 'beta_pro' && profile.beta_pro_expires_at) {
    const expiresAt = new Date(profile.beta_pro_expires_at);
    if (expiresAt < new Date()) {
      // Beta pro has expired, downgrade to free
      await supabase
        .from('profiles')
        .update({ tier: 'free' })
        .eq('user_id', userId);
      return 'free';
    }
  }

  return tier;
}

/**
 * Get the limits for a user based on their tier
 */
export async function getUserLimits(userId: string): Promise<TierLimits> {
  const tier = await getUserTier(userId);
  return TIER_LIMITS[tier];
}

/**
 * Check if user has pro access (pro or beta_pro)
 */
export async function isPro(userId: string): Promise<boolean> {
  const tier = await getUserTier(userId);
  return tier === 'pro' || tier === 'beta_pro';
}

/**
 * Check if user can create a new goal
 */
export async function canCreateGoal(userId: string): Promise<LimitCheckResult> {
  const limits = await getUserLimits(userId);
  
  if (limits.max_goals === -1) {
    return { allowed: true };
  }

  const { count, error } = await supabase
    .from('goals')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('archived', false);

  if (error) {
    console.error('Error counting goals:', error);
    return { allowed: false, reason: 'error' };
  }

  const currentCount = count || 0;
  
  if (currentCount >= limits.max_goals) {
    return {
      allowed: false,
      reason: 'goal_limit',
      currentCount,
      limit: limits.max_goals
    };
  }

  return { allowed: true, currentCount, limit: limits.max_goals };
}

/**
 * Check if user can create a new habit
 */
export async function canCreateHabit(userId: string): Promise<LimitCheckResult> {
  const limits = await getUserLimits(userId);
  
  if (limits.max_habits === -1) {
    return { allowed: true };
  }

  // Count habits across all user's active (non-archived) goals
  const { data: goals, error: goalsError } = await supabase
    .from('goals')
    .select('id')
    .eq('user_id', userId)
    .eq('archived', false);

  if (goalsError) {
    console.error('Error fetching goals:', goalsError);
    return { allowed: false, reason: 'error' };
  }

  if (!goals || goals.length === 0) {
    return { allowed: true, currentCount: 0, limit: limits.max_habits };
  }

  const goalIds = goals.map(g => g.id);
  
  const { count, error } = await supabase
    .from('habits')
    .select('*', { count: 'exact', head: true })
    .in('goal_id', goalIds)
    .eq('archived', false);

  if (error) {
    console.error('Error counting habits:', error);
    return { allowed: false, reason: 'error' };
  }

  const currentCount = count || 0;
  
  if (currentCount >= limits.max_habits) {
    return {
      allowed: false,
      reason: 'habit_limit',
      currentCount,
      limit: limits.max_habits
    };
  }

  return { allowed: true, currentCount, limit: limits.max_habits };
}

/**
 * Check if period_start is in the current month
 */
function isCurrentMonth(periodStart: string): boolean {
  const periodDate = new Date(periodStart);
  const now = new Date();
  return (
    periodDate.getMonth() === now.getMonth() &&
    periodDate.getFullYear() === now.getFullYear()
  );
}

/**
 * Check if user can run a coach analysis this month
 */
export async function canRunCoachAnalysis(userId: string): Promise<LimitCheckResult> {
  const limits = await getUserLimits(userId);
  
  if (limits.max_coach_analyses_per_month === -1) {
    return { allowed: true };
  }

  const { data: usage, error } = await supabase
    .from('usage_limits')
    .select('current_count, period_start')
    .eq('user_id', userId)
    .eq('resource_type', 'coach_analyses')
    .maybeSingle();

  if (error) {
    console.error('Error fetching usage:', error);
    return { allowed: false, reason: 'error' };
  }

  // If no record or period is from previous month, user can run analysis
  if (!usage || !isCurrentMonth(usage.period_start)) {
    return { allowed: true, usedThisMonth: 0, limit: limits.max_coach_analyses_per_month };
  }

  const usedThisMonth = usage.current_count || 0;
  
  if (usedThisMonth >= limits.max_coach_analyses_per_month) {
    return {
      allowed: false,
      reason: 'coach_limit',
      usedThisMonth,
      limit: limits.max_coach_analyses_per_month
    };
  }

  return { allowed: true, usedThisMonth, limit: limits.max_coach_analyses_per_month };
}

/**
 * Check if user can regenerate their development plan
 */
export async function canRegeneratePlan(userId: string): Promise<LimitCheckResult> {
  const limits = await getUserLimits(userId);
  
  if (limits.max_plan_generations === -1) {
    return { allowed: true };
  }

  const { data: usage, error } = await supabase
    .from('usage_limits')
    .select('current_count, period_start')
    .eq('user_id', userId)
    .eq('resource_type', 'plan_generations')
    .maybeSingle();

  if (error) {
    console.error('Error fetching usage:', error);
    return { allowed: false, reason: 'error' };
  }

  // If no record, user can generate plan
  if (!usage) {
    return { allowed: true };
  }

  const currentCount = usage.current_count || 0;
  
  if (currentCount >= limits.max_plan_generations) {
    return {
      allowed: false,
      reason: 'plan_limit'
    };
  }

  return { allowed: true };
}

/**
 * Check if user can access a specific integration
 */
export async function canAccessIntegration(userId: string, integration: string): Promise<boolean> {
  const limits = await getUserLimits(userId);
  return limits.integrations.includes(integration);
}

/**
 * Check if user can message mentors
 */
export async function canMessageMentor(userId: string): Promise<boolean> {
  const limits = await getUserLimits(userId);
  return limits.can_message_mentors;
}

/**
 * Check if user can offer mentorship
 */
export async function canOfferMentorship(userId: string): Promise<boolean> {
  const limits = await getUserLimits(userId);
  return limits.can_offer_mentorship;
}

/**
 * Increment usage count for a resource type
 * Handles monthly reset for applicable resources
 */
export async function incrementUsage(userId: string, resourceType: string): Promise<void> {
  // First, check if record exists
  const { data: existing, error: fetchError } = await supabase
    .from('usage_limits')
    .select('id, current_count, period_start')
    .eq('user_id', userId)
    .eq('resource_type', resourceType)
    .maybeSingle();

  if (fetchError) {
    console.error('Error fetching usage:', fetchError);
    return;
  }

  const now = new Date().toISOString();

  if (!existing) {
    // Insert new record
    const { error: insertError } = await supabase
      .from('usage_limits')
      .insert({
        user_id: userId,
        resource_type: resourceType,
        current_count: 1,
        period_start: now
      });

    if (insertError) {
      console.error('Error inserting usage:', insertError);
    }
    return;
  }

  // Check if we need to reset (new month)
  if (!isCurrentMonth(existing.period_start)) {
    // Reset and set to 1
    const { error: updateError } = await supabase
      .from('usage_limits')
      .update({
        current_count: 1,
        period_start: now,
        updated_at: now
      })
      .eq('id', existing.id);

    if (updateError) {
      console.error('Error resetting usage:', updateError);
    }
    return;
  }

  // Increment existing count
  const { error: updateError } = await supabase
    .from('usage_limits')
    .update({
      current_count: (existing.current_count || 0) + 1,
      updated_at: now
    })
    .eq('id', existing.id);

  if (updateError) {
    console.error('Error incrementing usage:', updateError);
  }
}

/**
 * Get user's current usage summary
 */
export async function getUserUsageSummary(userId: string): Promise<{
  tier: UserTier;
  limits: TierLimits;
  goals: { current: number; limit: number | null };
  habits: { current: number; limit: number | null };
  coachAnalyses: { usedThisMonth: number; limit: number | null };
  planGenerations: { used: number; limit: number | null };
}> {
  const tier = await getUserTier(userId);
  const limits = TIER_LIMITS[tier];

  // Get goal count (only non-archived)
  const { count: goalCount } = await supabase
    .from('goals')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('archived', false);

  // Get habit count (only non-archived)
  const { data: goals } = await supabase
    .from('goals')
    .select('id')
    .eq('user_id', userId)
    .eq('archived', false);

  let habitCount = 0;
  if (goals && goals.length > 0) {
    const { count } = await supabase
      .from('habits')
      .select('*', { count: 'exact', head: true })
      .in('goal_id', goals.map(g => g.id))
      .eq('archived', false);
    habitCount = count || 0;
  }

  // Get usage limits
  const { data: usageData } = await supabase
    .from('usage_limits')
    .select('resource_type, current_count, period_start')
    .eq('user_id', userId);

  const coachUsage = usageData?.find(u => u.resource_type === 'coach_analyses');
  const planUsage = usageData?.find(u => u.resource_type === 'plan_generations');

  return {
    tier,
    limits,
    goals: {
      current: goalCount || 0,
      limit: limits.max_goals === -1 ? null : limits.max_goals
    },
    habits: {
      current: habitCount,
      limit: limits.max_habits === -1 ? null : limits.max_habits
    },
    coachAnalyses: {
      usedThisMonth: coachUsage && isCurrentMonth(coachUsage.period_start) ? coachUsage.current_count : 0,
      limit: limits.max_coach_analyses_per_month === -1 ? null : limits.max_coach_analyses_per_month
    },
    planGenerations: {
      used: planUsage?.current_count || 0,
      limit: limits.max_plan_generations === -1 ? null : limits.max_plan_generations
    }
  };
}
